import { computeAdultsAndChildren } from '../helpers';
import { usePhoneFromReferential } from '../hooks';
import { useBookingStore } from '../store';

import type {
  BasketBeneficiaryPayload,
  BasketRoomPayload,
  Exact,
} from '~~/domains/graphql';
import { useUserStore } from '~~/domains/user';

function addSpecialOptionsToRoom(
  specialOptions: string[],
  payload: BasketRoomPayload,
) {
  if (specialOptions.length) {
    if (!Array.isArray(payload.options)) {
      payload.options = [];
    }

    payload.options.push(
      ...specialOptions.map(option => ({
        code: option,
        quantity: 1,
      })),
    );
  }
}

export function useBasketPayload() {
  const bookingStore = useBookingStore();
  const userStore = useUserStore();

  return computed<
    | Exact<{
        hotelCode: string;
        basketId: string;
        rooms: BasketRoomPayload | BasketRoomPayload[];
      }>
    | undefined
  >(() => {
    if (!bookingStore.hotelId) {
      return;
    }

    if (
      !bookingStore.dates ||
      (bookingStore.dates &&
        (!bookingStore.dates.nights || !bookingStore.dates.dateIn))
    ) {
      return;
    }

    const rooms = Array.from(
      bookingStore.rooms.values(),
    ).map<BasketRoomPayload>(bookingRoom => {
      const { adults, childrenAges } = computeAdultsAndChildren(
        bookingRoom.adults,
        bookingRoom.childrenAges,
        bookingStore.maxChildrenAge,
      );

      const payload = {
        adults,
        childrenAges,
        offerCode:
          userStore.isLogged || bookingStore.wantSignup
            ? bookingRoom.memberOfferId ?? bookingRoom.publicOfferId
            : bookingRoom.publicOfferId,
        period: bookingStore.dates as { dateIn: string; nights: number },
        productCode: bookingRoom.productCode,
        basketRoomId: bookingRoom.basketRoomId,
      } as BasketRoomPayload;

      if (bookingRoom.options) {
        payload.options = Array.from(bookingRoom.options).map(
          ([_, option]) => ({
            code: option.code,
            quantity: option.quantity,
          }),
        );
      }

      addSpecialOptionsToRoom(bookingStore.specialOptions, payload);

      if (bookingStore.beneficiary) {
        payload.beneficiary = basketBeneficiaryPayload();
      }

      return payload;
    });

    return {
      hotelCode: bookingStore.hotelId,
      basketId: bookingStore.basketId ?? '',
      rooms,
    };
  });
}

export function basketBeneficiaryPayload(): BasketBeneficiaryPayload {
  const bookingStore = useBookingStore();
  const { getPrefixFromAlpha2Code } = usePhoneFromReferential();

  if (!bookingStore.beneficiary) {
    return {} as BasketBeneficiaryPayload;
  }

  return {
    address1: bookingStore.beneficiary.address1,
    address2: bookingStore.beneficiary.address2,
    address3: bookingStore.beneficiary.address3,
    address4: bookingStore.beneficiary.address4,
    city: bookingStore.beneficiary.city,
    civilityCode: bookingStore.beneficiary.civilityCode,
    countryCode: bookingStore.beneficiary.countryCode,
    currency: bookingStore.currentCurrency,
    email: bookingStore.beneficiary.email,
    firstName: bookingStore.beneficiary.firstName,
    lastName: bookingStore.beneficiary.lastName,
    nationalityCode: bookingStore.beneficiary.nationalityCode,
    phone: {
      number: bookingStore.beneficiary.phone.number,
      prefix: getPrefixFromAlpha2Code(bookingStore.beneficiary.phone.prefix),
    },
    stateCode: bookingStore.beneficiary.stateCode,
    zipcode: bookingStore.beneficiary.zipcode,
  };
}
